// ─── Shared page-access logic ─────────────────────────────────────────────────
// Used by BOTH Sidebar.js (to decide which nav items to render) and App.js (to
// decide whether to actually render the page or an "Access Restricted" screen).
// These two call sites used to keep their own copies of this function — kept in
// one place now so they can never drift apart.
//
// 'digitalmedia' is special-cased: it's a read-only analytics page built for a
// "Digital Media" role (an ads/marketing person who needs to see lead quality,
// status, and notes, but nothing else). Its access is hardcoded here rather
// than left to the generic per-role accessControl matrix in Settings, because
// that matrix defaults to "allow all pages" whenever a role has no explicit
// entry saved yet — that default-allow fallback must never apply to this page.
export function canAccessPage(currentUser, company, pageId) {
  if (!currentUser) return true;
  const role = currentUser.role;

  // Admins and superadmins always have full access.
  if (role === 'admin' || role === 'superadmin') return true;

  // Digital Media role: locked to the Digital Media page, full stop. No
  // accessControl override can grant it other pages, and nothing else can
  // grant it this one.
  if (role === 'digital_media') return pageId === 'digitalmedia';
  if (pageId === 'digitalmedia') return false;

  // Test Prep role: can only access testprep page by default.
  // Admin can override via accessControl to grant additional pages.
  if (role === 'test_prep') {
    const accessControl = company?.accessControl || {};
    const userOverride = accessControl?.users?.[currentUser.id];
    if (userOverride?.deny?.includes(pageId)) return false;
    if (userOverride?.allow?.includes(pageId)) return true;
    const rolePages = accessControl['test_prep'];
    if (rolePages) return rolePages.includes(pageId);
    return pageId === 'testprep';
  }

  const accessControl = company?.accessControl || {};
  // Per-user overrides take priority over role defaults.
  const userOverride = accessControl?.users?.[currentUser.id];
  if (userOverride?.deny?.includes(pageId)) return false;
  if (userOverride?.allow?.includes(pageId)) return true;

  // Fall back to role-level defaults.
  const rolePages = accessControl[role];
  if (rolePages) return rolePages.includes(pageId);

  // No accessControl entry for this role yet — default to allowing everything
  // except admin-only pages (those are filtered separately via item.adminOnly).
  return true;
}
